import { z } from 'zod'
import { paginated } from './api.ts'

/**
 * A produced file, as a governed record.
 *
 * The bytes and the row are separate on purpose (ART-001, ART-003): the row carries
 * provenance — which run made it, from which step, on whose behalf — and travels through
 * transcripts and lists, while the bytes are fetched once, deliberately, through a path that
 * can refuse.
 */

/** Where the bytes came from. Provenance is part of the record, not a guess made later. */
export const ArtifactOriginSchema = z.enum(['local_tool', 'remote_agent', 'sandbox', 'upload'])
export type ArtifactOrigin = z.infer<typeof ArtifactOriginSchema>

/**
 * ART-002's gate, reduced to what this deployment can honestly report.
 *
 * `pending` is not "probably fine, not looked at yet" — it is refused at download, because an
 * artifact that has not been checked and an artifact that has been cleared must not be the
 * same thing to a user about to open it.
 */
export const ArtifactScanStatusSchema = z.enum(['pending', 'clean', 'rejected'])
export type ArtifactScanStatus = z.infer<typeof ArtifactScanStatusSchema>

export const ArtifactSchema = z.object({
  id: z.uuid(),
  runId: z.uuid().nullable(),
  stepId: z.uuid().nullable(),
  conversationId: z.uuid().nullable(),
  createdBy: z.uuid().nullable(),
  name: z.string(),
  mediaType: z.string(),
  byteSize: z.number().int().nonnegative(),
  /** Content address. Two runs that produced identical bytes are visibly the same output. */
  sha256: z.string(),
  origin: ArtifactOriginSchema,
  originDetail: z.record(z.string(), z.unknown()),
  scanStatus: ArtifactScanStatusSchema,
  scanDetail: z.string().nullable(),
  /**
   * Whether `GET /api/artifacts/{id}/content` will serve this. Derived from `scanStatus`, and
   * exposed so a client renders the right affordance without reimplementing the rule — a
   * download button that 412s is a worse experience than one that is not there.
   */
  downloadable: z.boolean(),
  createdAt: z.iso.datetime()
  // Deliberately absent: `storagePath`. It is a server filesystem path with no client use and
  // real disclosure value.
})
export type Artifact = z.infer<typeof ArtifactSchema>

export const ArtifactListResponseSchema = paginated(ArtifactSchema)
export type ArtifactListResponse = z.infer<typeof ArtifactListResponseSchema>
